import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { HttpConfig } from "../http-config";
import { Observable } from "rxjs";
import { EnvService } from "gematik-form-library";

export interface Endpoint {
  id: number;
  name: string;
  url: string;
  roles: string;
  uuid: string;
  labelEn: string;
  labelDe: string;
  icon: string;
  enabledInMyServices: Boolean;
  link: string;
  retentionDays: number;
}

export interface CrmRight {
  id: string;
  name: string;
}

export interface DirectOnboardingConfig {
  id: string | null;
  systemName: string;
  crmOrgRoleGemId: string;
  crmIdentityRoleGemId: string;
  validFromDate: string | null;
  validToDate: string | null;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  notes: string;
  url: string;
  createdBy?: string;
  createdOn?: string;
  modifiedBy?: string;
  modifiedOn?: string;
}

export class UwlHelpConfig {
  id: number;
  helpkey: string;
  lang: string;
  title: string;
  html: string;

  constructor() {
    this.id = null;
    this.helpkey = "";
    this.lang = "";
    this.title = "";
    this.html = "";
  }
}

@Injectable({
  providedIn: "root",
})
export class UwlService {
  httpConfig: HttpConfig = {
    headers: new HttpHeaders(),
    observe: "response",
  };

  constructor(
    private http: HttpClient,
    private formBuilder: UntypedFormBuilder,
    private env: EnvService,
  ) {}

  createUpdateEndpoint(endpoint: Endpoint): Observable<HttpConfig> {
    return this.http.post(this.env.backendUrl + "/uwl/endpoint", endpoint, this.httpConfig);
  }

  deleteEndpoint(id: number): Observable<HttpConfig> {
    return this.http.delete(this.env.backendUrl + "/uwl/endpoint/" + id, this.httpConfig);
  }

  findAllEndpoints(): Observable<HttpConfig> {
    return this.http.get(this.env.backendUrl + "/uwl/endpoint", this.httpConfig);
  }

  createUpdateHelpConfig(config: UwlHelpConfig): Observable<HttpConfig> {
    return this.http.post(this.env.backendUrl + "/uwl/help-config", config, this.httpConfig);
  }

  deleteHelpConfig(config: UwlHelpConfig): Observable<HttpConfig> {
    return this.http.delete(this.env.backendUrl + "/uwl/help-config/" + config.id, this.httpConfig);
  }

  findHelpConfig(id: string, lang: string): Observable<HttpConfig> {
    return this.http.get(
      this.env.backendUrl + "/uwl/help-config/" + id + "/" + lang,
      this.httpConfig,
    );
  }

  findAllHelpConfigs(): Observable<HttpConfig> {
    return this.http.get(this.env.backendUrl + "/uwl/help-config", this.httpConfig);
  }

  mapUwlEndpointToForm(endpoint: Endpoint) {
    if (endpoint !== null) {
      return this.formBuilder.group(endpoint);
    } else {
      const object: Endpoint = {
        id: -1,
        url: "",
        name: "",
        roles: "",
        uuid: "",
        labelEn: "",
        labelDe: "",
        icon: "",
        enabledInMyServices: false,
        link: "",
        retentionDays: 0,
      };
      return this.formBuilder.group(object);
    }
  }

  mapFormToUwlHelpConfig(form: UntypedFormGroup) {
    return form.getRawValue();
  }

  mapUwlHelpConfigToForm(config: UwlHelpConfig) {
    if (config !== null) {
      return this.formBuilder.group(config);
    } else {
      return this.formBuilder.group(new UwlHelpConfig());
    }
  }

  mapFormToUwlEndpoint(form: UntypedFormGroup) {
    return form.getRawValue();
  }

  getTemplates(): Observable<HttpConfig> {
    return this.http.get(this.env.notificationsBackendUrl + "/templates", this.httpConfig);
  }

  getCrmRights(email: string): Observable<HttpConfig> {
    return this.http.get(`${this.env.backendUrl}/uwl/crm/rights/${email}`, this.httpConfig);
  }

  createDirectOnboardingConfig(config: DirectOnboardingConfig): Observable<HttpConfig> {
    const writeDto = this.toDirectOnboardingWriteDto(config);
    return this.http.post(
      `${this.env.backendUrl}/uwl/direct-onboarding-configurations`,
      writeDto,
      this.httpConfig,
    );
  }

  updateDirectOnboardingConfig(config: DirectOnboardingConfig): Observable<HttpConfig> {
    const writeDto = this.toDirectOnboardingWriteDto(config);
    return this.http.put(
      `${this.env.backendUrl}/uwl/direct-onboarding-configurations/${config.id}`,
      writeDto,
      this.httpConfig,
    );
  }

  findAllDirectOnboardingConfigs(): Observable<HttpConfig> {
    return this.http.get(
      `${this.env.backendUrl}/uwl/direct-onboarding-configurations`,
      this.httpConfig,
    );
  }

  deleteDirectOnboardingConfig(id: string): Observable<HttpConfig> {
    return this.http.delete(
      `${this.env.backendUrl}/uwl/direct-onboarding-configurations/${id}`,
      this.httpConfig,
    );
  }

  mapDirectOnboardingConfigToForm(config: DirectOnboardingConfig) {
    const base = config !== null ? config : {
      id: null,
      systemName: "",
      crmOrgRoleGemId: "",
      crmIdentityRoleGemId: "",
      validFromDate: null,
      validToDate: null,
      title: "",
      titleEn: "",
      description: "",
      descriptionEn: "",
      notes: "",
      url: "",
    };
    return this.formBuilder.group({
      ...base,
      validFromDatePicker: [base.validFromDate ? new Date(base.validFromDate) : null],
      validFromTimePicker: [this.extractTime(base.validFromDate)],
      validToDatePicker: [base.validToDate ? new Date(base.validToDate) : null],
      validToTimePicker: [this.extractTime(base.validToDate)],
    });
  }

  mapFormToDirectOnboardingConfig(form: UntypedFormGroup): DirectOnboardingConfig {
    return form.getRawValue();
  }

  private extractTime(dateStr: string | null): string | null {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private toDirectOnboardingWriteDto(config: DirectOnboardingConfig) {
    return {
      systemName: config.systemName,
      crmOrgRoleGemId: config.crmOrgRoleGemId,
      crmIdentityRoleGemId: config.crmIdentityRoleGemId,
      validFromDate: config.validFromDate,
      validToDate: config.validToDate,
      title: config.title,
      titleEn: config.titleEn,
      description: config.description,
      descriptionEn: config.descriptionEn,
      notes: config.notes,
    };
  }
}
